/**
 * RunDocumentViewer — the triggering document, rendered by FORMAT.
 *
 * The review station's left pane used the placeholder ``PDFViewer`` (a sandboxed
 * iframe that Chromium refuses to paint for a ``blob:`` URL → the broken-file
 * box). This instead reuses the SAME production viewers the working area mounts
 * (``@ando/ui/editors``: react-pdf canvas + image viewer), dispatched by the
 * fetched blob's MIME — so PDFs and images actually render, and anything else
 * degrades to a clean download card (never a broken preview).
 *
 * DRY: every run document is opened through the canonical document edit-session
 * contract, exactly like ArtifactPane. The backend resolves ACL, MIME and MinIO
 * coordinates and returns ``/documents/{id}/file`` as the read URL. This works
 * for uploads, repository drawings and generated quotation PDFs without a
 * flow-specific storage/proxy path.
 */

import { cn } from "@ando/ui";
// Leaf subpath (zero-dep) — NEVER the ``@ando/ui/editors`` barrel here: an
// eager barrel import drags node_modules CSS into the module graph and
// breaks the vitest transform (see the lazy-load note below).
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { Download, FileText, Loader2, TriangleAlert } from "lucide-react";
import * as React from "react";

import { useEditSession } from "@/features/workspace/hooks/useEditSession";

// Lazy-load the heavy editor viewers (react-pdf / image) so the run-surface
// bundle stays lean AND the module graph never EAGERLY pulls the editors barrel
// (which imports node_modules CSS that breaks the vitest transform). Loaded only
// the moment a PDF/image actually renders — SOTA code-splitting.
const PDFViewerInline = React.lazy(() =>
  import("@ando/ui/editors").then((m) => ({ default: m.PDFViewerInline })),
);
const ImageViewerInline = React.lazy(() =>
  import("@ando/ui/editors").then((m) => ({ default: m.ImageViewerInline })),
);

// Extension → MIME fallback: the auth-proxy sometimes streams the bytes as
// ``application/octet-stream`` (or no content-type), so a generic blob type is
// backfilled from the filename before we pick a viewer.
const EXT_MIME: Record<string, string> = {
  pdf: "application/pdf",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  webp: "image/webp",
  svg: "image/svg+xml",
  tif: "image/tiff",
  tiff: "image/tiff",
  bmp: "image/bmp",
};

function guessMimeFromName(filename: string | undefined): string | undefined {
  const ext = filename?.split(".").pop()?.toLowerCase();
  return ext ? EXT_MIME[ext] : undefined;
}

export interface RunDocumentViewerProps {
  readonly contentId: string | undefined;
  readonly filename?: string;
  readonly className?: string;
  /** Authenticated/presigned artifact URL. Uses the same renderer as repository
   * documents without manufacturing a content item just for a live preview. */
  readonly readUrl?: string;
  readonly mimeType?: string;
}

export function RunDocumentViewer({
  contentId,
  filename,
  className,
  readUrl,
  mimeType,
}: RunDocumentViewerProps): React.JSX.Element {
  const { i18n } = useLingui();
  const editSession = useEditSession(readUrl ? undefined : contentId);
  const directSession = readUrl ? {
    session_id: "artifact-preview",
    doc_id: contentId ?? "artifact-preview",
    mime_type: mimeType ?? "application/pdf",
    filename: filename ?? "Anteprima.pdf",
    read_url: readUrl,
    save_url: "",
    save_method: "PUT" as const,
    etag: null,
    lock_token: null,
    expires_at: new Date(Date.now() + 300_000).toISOString(),
  } : undefined;
  const session = directSession ?? editSession.data;
  const isLoading = !readUrl && editSession.isLoading;
  const isError = !readUrl && editSession.isError;
  const name = filename || i18n._(msg`Documento`);

  const shell = (child: React.ReactNode) => (
    <div className={cn("flex h-full w-full items-center justify-center bg-paper", className)}>
      {child}
    </div>
  );

  if (!contentId && !readUrl) {
    return shell(
      <p className="text-[13px] text-ink-4">
        <Trans>Nessun documento allegato.</Trans>
      </p>,
    );
  }
  if (isLoading || (!session && !isError)) {
    return shell(
      <span className="inline-flex items-center gap-2 text-[13px] text-ink-3">
        <Loader2 size={15} aria-hidden strokeWidth={2} className="animate-spin text-ando-orange" />
        <Trans>Carico il documento…</Trans>
      </span>,
    );
  }
  if (isError || !session) {
    return shell(
      <span className="inline-flex items-center gap-2 text-[13px] text-ink-3">
        <TriangleAlert size={15} aria-hidden strokeWidth={2} className="text-red-500" />
        <Trans>Impossibile caricare il documento.</Trans>
      </span>,
    );
  }

  const usable =
    session.mime_type && session.mime_type !== "application/octet-stream"
      ? session.mime_type
      : undefined;
  const mime = usable ?? guessMimeFromName(filename) ?? "application/octet-stream";
  const viewerSession = { ...session, filename: session.filename || name, mime_type: mime };

  const loading = (
    <span className="inline-flex items-center gap-2 text-[13px] text-ink-3">
      <Loader2 size={15} aria-hidden strokeWidth={2} className="animate-spin text-ando-orange" />
      <Trans>Carico il documento…</Trans>
    </span>
  );

  if (mime === "application/pdf") {
    return (
      <div className={cn("h-full w-full", className)}>
        <React.Suspense fallback={shell(loading)}>
          <PDFViewerInline session={viewerSession} />
        </React.Suspense>
      </div>
    );
  }
  if (mime.startsWith("image/")) {
    return (
      <div className={cn("h-full w-full", className)}>
        <React.Suspense fallback={shell(loading)}>
          <ImageViewerInline session={viewerSession} />
        </React.Suspense>
      </div>
    );
  }

  // Office / text / unknown: a clean download card (no broken inline preview).
  return shell(
    <div className="flex flex-col items-center gap-3 px-6 text-center">
      <FileText size={28} aria-hidden strokeWidth={1.5} className="text-ink-4" />
      <p className="max-w-[28ch] truncate font-medium text-[13px] text-ink-2">{name}</p>
      <a
        href={session.read_url}
        download={name}
        className="inline-flex items-center gap-1.5 rounded-md border border-hairline px-3 py-1.5 font-medium text-[12px] text-ink-2 transition-colors hover:border-ando-orange hover:text-ink-1"
      >
        <Download size={13} aria-hidden strokeWidth={2} />
        <Trans>Scarica</Trans>
      </a>
    </div>,
  );
}
